import React, { Suspense, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HashRouter, Route, Redirect, Switch } from "react-router-dom";
import { IdleTimerProvider } from "react-idle-timer";
import { TopBar as TopBarTypes } from "../redux/TopBar/types";
import { VIEWS } from "../redux/TopBar/constants";
import { AuthActions } from "../redux/Auth/actions";
import Spinner from "../components/views/Spinner";
import Lander from "../components/views/Lander";
import Modal from "../components/views/Modal";
import Toaster from "../components/views/Toaster";
import Confirm from "../components/views/Confirm";
import TopBar from "../components/views/TopBar";
import UpdateBanner from "../components/views/UpdateBanner";
import ReauthBanner from "../components/views/ReauthBanner";
import TermsChangedNotice from "../components/views/TermsChangedNotice";
import { SettingsSelectors } from "../redux/Settings/selectors";
import { Tooltip } from "react-tooltip";
import ClientPool from "src/components/views/ClientPool";
import PostTrade from "src/components/views/PostTrade";
import Dashboard from "src/components/views/Dashboard";
import CptyManagement from "src/components/views/CptyManagement";
import CptyDetail from "src/components/views/CptyManagement/CptyDetail";
import FeatureFlags from "src/components/views/FeatureFlags";
import UserManagement from "src/components/views/UserManagement";
import CptyUserManagement from "src/components/views/UserManagement/CptyUserManagement";
import UserHistory from "src/components/views/UserManagement/UserHistory";
import PlatformSettings from "src/components/views/PlatformSettings";
import Security from "src/components/views/Security";
import Audit from "src/components/views/Audit";
import { AuthSelectors } from "src/redux/Auth/selectors";
const Confirmations = lazy(() => import("../components/views/Confirmations"));
const Transactions = lazy(() => import("../components/views/Transactions"));
const Inventory = lazy(() => import("../components/views/Inventory"));
const Availability = lazy(() => import("../components/views/Availability"));
const Recalls = lazy(() => import("../components/views/Recalls"));

/**
 * Trading routes for trader and auditor roles (TruWeb Portal).
 */
const TradingRoutes = ({ usesClientPool, enabledRecallTrade, lastView }) => (
    <Switch>
        <Route path="/confirmations" component={Confirmations} />
        <Route path="/inventory" component={Inventory} />
        <Route path="/availability" component={Availability} />
        <Route path="/negotiations" component={Transactions} />
        {usesClientPool ? (
            <Route path="/client-pool" component={ClientPool} />
        ) : (
            <Redirect exact from="/client-pool" to="/negotiations" />
        )}
        {enabledRecallTrade ? (
            <Route path="/recalls" component={Recalls} />
        ) : (
            <Redirect exact from="/recalls" to="/negotiations" />
        )}
        <Route path="/post-trade" component={PostTrade} />
        <Route path="/dashboard" component={Dashboard} />
        <Redirect exact from="/transactions" to="/negotiations" />
        <Redirect exact from="/" to={lastView} />
    </Switch>
);

/**
 * Admin routes for supervisor role (Admin Portal).
 * Supervisor sees the platform-wide trading dashboard only.
 */
const SupervisorRoutes = () => (
    <Switch>
        <Route path="/dashboard" component={Dashboard} />
        <Redirect exact from="/" to="/dashboard" />
        <Redirect to="/dashboard" />
    </Switch>
);

/**
 * Admin routes for operator role (Admin Portal).
 * Operator manages counterparties.
 */
const OperatorRoutes = () => (
    <Switch>
        <Route exact path="/cpties" component={CptyManagement} />
        <Route path="/cpties/:code" component={CptyDetail} />
        <Route exact path="/users" component={UserManagement} />
        <Route path="/users/:cptyCode/:userId/history" component={UserHistory} />
        <Route path="/users/:cptyCode" component={CptyUserManagement} />
        <Route path="/feature-flags" component={FeatureFlags} />
        <Route path="/platform" component={PlatformSettings} />
        <Route path="/security" component={Security} />
        <Route path="/audit" component={Audit} />
        <Redirect exact from="/" to="/cpties" />
        <Redirect to="/cpties" />
    </Switch>
);

const Router = () => {
    const dispatch = useDispatch();
    const { "enable-recall-trade": enabledRecallTrade } = useSelector(
        SettingsSelectors.FeatureFlags
    );
    const usesClientPool = useSelector(AuthSelectors.UsesClientPool);
    const userRole = useSelector(AuthSelectors.UserRole);
    const onIdle = () => {
        console.log("Logging out after a timeout");
        dispatch(AuthActions.Logout());
    };

    const lastView =
        localStorage.getItem(TopBarTypes.SET_VIEW) || VIEWS.TRANSACTIONS;

    const renderRoutes = () => {
        switch (userRole) {
            case "supervisor":
                return <SupervisorRoutes />;
            case "operator":
                return <OperatorRoutes />;
            default:
                return (
                    <TradingRoutes
                        usesClientPool={usesClientPool}
                        enabledRecallTrade={enabledRecallTrade}
                        lastView={lastView}
                    />
                );
        }
    };

    return (
        <HashRouter>
            <Lander>
                <IdleTimerProvider
                    timeout={1000 * 60 * 30} // 30 minutes in milliseconds
                    onIdle={onIdle}
                >
                    <UpdateBanner />
                    <ReauthBanner />
                    <TermsChangedNotice />
                    <TopBar />
                    <Suspense fallback={<Spinner />}>
                        {renderRoutes()}
                    </Suspense>
                    <Modal />
                    <Spinner />
                    <Toaster />
                    <Confirm />
                    <Tooltip
                        id="tru-ui-tooltip"
                        multiline={true}
                        className="truUiTooltip"
                    />
                </IdleTimerProvider>
            </Lander>
        </HashRouter>
    );
};

export default Router;
